import { motion } from 'framer-motion';
import { FormStepProps } from '../../../types/applicationTypes';

export default function Step3ClassroomRequest({ data, errors, updateData }: FormStepProps) {
  const classroomErrors = errors?.classroomRequest ?? {};

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-lg transition-colors ${
      hasError
        ? 'border-red-500 focus:ring-2 focus:ring-red-300 focus:border-red-500'
        : 'border-gray-300 focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300'
    }`;

  const handleInputChange = (field: keyof typeof data.classroomRequest, value: string) => {
    updateData({
      classroomRequest: {
        ...data.classroomRequest,
        [field]: value
      }
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center mb-6">
        <div className="bg-yellow-100 p-3 rounded-full mr-4">
          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-yellow-600">
          Classroom Request
        </h3>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <p className="text-blue-700 text-sm">
          Please select which classroom and days you are interested in.
        </p>
      </div>

      <div className="space-y-6">
        {/* Classroom Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Classroom applying for*
          </label>
          <div className="relative">
            <select
              value={data.classroomRequest.classroom}
              onChange={(e) => handleInputChange('classroom', e.target.value)}
              className={`${inputClass(Boolean(classroomErrors.classroom))} appearance-none`}
              required
              aria-invalid={Boolean(classroomErrors.classroom)}
            >
              <option value="">Select Classroom</option>
              <option value="Starfish Room">Starfish Room (6 Months - 24 months)</option>
              <option value="Seal Room">Seal Room (2 - 3 years)</option>
              <option value="Dolphin or Orca Room">Dolphin or Orca Room (3 - 5 years)</option>
            </select>
            <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          {classroomErrors.classroom && (
            <p className="mt-1 text-sm text-red-600">{classroomErrors.classroom}</p>
          )}
        </div>

        {/* Requested Days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Requested Days (Note: we typically offer care for 2, 3, or 5 full days only, take 4 days on special request)*
          </label>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="requestedDays"
                value="2-days-mon-tue"
                checked={data.classroomRequest.requestedDays === '2-days-mon-tue'}
                onChange={(e) => handleInputChange('requestedDays', e.target.value)}
                className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-300"
                aria-invalid={Boolean(classroomErrors.requestedDays)}
              />
              <span className="ml-3 text-sm text-gray-700">2 days (Monday, Tuesday)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="requestedDays"
                value="2-days-thu-fri"
                checked={data.classroomRequest.requestedDays === '2-days-thu-fri'}
                onChange={(e) => handleInputChange('requestedDays', e.target.value)}
                className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-300"
                aria-invalid={Boolean(classroomErrors.requestedDays)}
              />
              <span className="ml-3 text-sm text-gray-700">2 days (Thursday, Friday)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="requestedDays"
                value="3-days-wed-thu-fri"
                checked={data.classroomRequest.requestedDays === '3-days-wed-thu-fri'}
                onChange={(e) => handleInputChange('requestedDays', e.target.value)}
                className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-300"
                aria-invalid={Boolean(classroomErrors.requestedDays)}
              />
              <span className="ml-3 text-sm text-gray-700">3 days (Wednesday, Thursday, Friday)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="requestedDays"
                value="3-days-mon-tue-wed"
                checked={data.classroomRequest.requestedDays === '3-days-mon-tue-wed'}
                onChange={(e) => handleInputChange('requestedDays', e.target.value)}
                className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-300"
                aria-invalid={Boolean(classroomErrors.requestedDays)}
              />
              <span className="ml-3 text-sm text-gray-700">3 days (Monday, Tuesday, Wednesday)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="requestedDays"
                value="5-days-mon-fri"
                checked={data.classroomRequest.requestedDays === '5-days-mon-fri'}
                onChange={(e) => handleInputChange('requestedDays', e.target.value)}
                className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-300"
                aria-invalid={Boolean(classroomErrors.requestedDays)}
              />
              <span className="ml-3 text-sm text-gray-700">5 days (Monday-Friday)</span>
            </label>
          </div>
          {classroomErrors.requestedDays && (
            <p className="mt-1 text-sm text-red-600">{classroomErrors.requestedDays}</p>
          )}
        </div>

        {/* Requested Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Requested Start Date
          </label>
          <input
            type="date"
            value={data.classroomRequest.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            className={inputClass(false)}
          />
        </div>

        {/* Space Preference */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Preference of Space for Full-Time Applicants*
          </label>
          <div className="space-y-3">
            <label className="flex items-start">
              <input
                type="radio"
                name="spacePreference"
                value="flexible"
                checked={data.classroomRequest.spacePreference === 'flexible'}
                onChange={(e) => handleInputChange('spacePreference', e.target.value)}
                className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-300 mt-1"
                aria-invalid={Boolean(classroomErrors.spacePreference)}
              />
              <span className="ml-3 text-sm text-gray-700">Yes, I am flexible in taking 2 or 3 days until a full-time space becomes available.</span>
            </label>
            <label className="flex items-start">
              <input
                type="radio"
                name="spacePreference"
                value="wait-fulltime"
                checked={data.classroomRequest.spacePreference === 'wait-fulltime'}
                onChange={(e) => handleInputChange('spacePreference', e.target.value)}
                className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-300 mt-1"
                aria-invalid={Boolean(classroomErrors.spacePreference)}
              />
              <span className="ml-3 text-sm text-gray-700">No, I will wait until a full-time space becomes available.</span>
            </label>
          </div>
          {classroomErrors.spacePreference && (
            <p className="mt-1 text-sm text-red-600">{classroomErrors.spacePreference}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
